import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Button,
  ButtonGroup
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  CheckCircle,
  Timer,
  StarRate,
  Assessment,
  CalendarToday,
  AccessTime,
  EventAvailable
} from '@mui/icons-material';
import moment from 'moment';

const PerformanceMetrics = ({ attendance = [] }) => {
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'quarter'
  const [metrics, setMetrics] = useState({
    consistencyScore: 0,
    punctualityScore: 0,
    productivityScore: 0,
    overallScore: 0,
    trends: {},
    weeklyData: [],
    achievements: []
  });

  // Calculate performance metrics
  const calculateMetrics = () => {
    if (!attendance || attendance.length === 0) {
      return {
        consistencyScore: 0,
        punctualityScore: 0,
        productivityScore: 0,
        overallScore: 0,
        trends: {},
        weeklyData: [],
        achievements: []
      };
    }

    const now = moment();
    let startDate, endDate;

    switch (timeRange) {
      case 'week':
        startDate = now.clone().startOf('week');
        endDate = now.clone().endOf('week');
        break;
      case 'quarter':
        startDate = now.clone().startOf('quarter');
        endDate = now.clone().endOf('quarter');
        break;
      default: // month
        startDate = now.clone().startOf('month');
        endDate = now.clone().endOf('month');
    }

    const relevantAttendance = attendance.filter(entry => {
      const entryDate = moment(entry.date, 'YYYY-MM-DD');
      return entryDate.isBetween(startDate, endDate, null, '[]');
    });

    // 1. Consistency Score (0-100)
    const expectedWorkdays = getExpectedWorkdays(startDate, endDate);
    const actualWorkdays = new Set(relevantAttendance.map(entry => entry.date)).size;
    const consistencyScore = expectedWorkdays > 0 ? Math.round((actualWorkdays / expectedWorkdays) * 100) : 0;

    // 2. Punctuality Score (0-100)
    const punctualityScore = calculatePunctualityScore(relevantAttendance, startDate, endDate);

    // 3. Productivity Score (0-100)
    const productivityScore = calculateProductivityScore(relevantAttendance);

    // 4. Overall Score (weighted average)
    const overallScore = Math.round(
      (consistencyScore * 0.4) + (punctualityScore * 0.3) + (productivityScore * 0.3)
    );

    // 5. Weekly trends
    const weeklyData = generateWeeklyData(relevantAttendance, startDate, endDate);

    // 6. Achievements
    const achievements = calculateAchievements(relevantAttendance, {
      consistencyScore,
      punctualityScore,
      productivityScore,
      overallScore
    });

    return {
      consistencyScore,
      punctualityScore,
      productivityScore,
      overallScore,
      trends: {
        consistency: consistencyScore >= 85 ? 'up' : consistencyScore >= 70 ? 'stable' : 'down',
        punctuality: punctualityScore >= 85 ? 'up' : punctualityScore >= 70 ? 'stable' : 'down',
        productivity: productivityScore >= 85 ? 'up' : productivityScore >= 70 ? 'stable' : 'down'
      },
      weeklyData,
      achievements
    };
  };

  const getExpectedWorkdays = (start, end) => {
    let count = 0;
    const current = start.clone();
    
    while (current.isSameOrBefore(end, 'day')) {
      // Count weekdays only (Monday = 1, Sunday = 0)
      if (current.day() !== 0 && current.day() !== 6 && current.isSameOrBefore(moment(), 'day')) {
        count++;
      }
      current.add(1, 'day');
    }
    return count;
  };

  const calculatePunctualityScore = (attendanceData, startDate, endDate) => {
    if (attendanceData.length === 0) return 0;

    const standardStartTime = moment('09:00', 'HH:mm');
    let onTimeCount = 0;

    // Group by date to get first entry of each day
    const dailyFirstEntries = {};
    attendanceData.forEach(entry => {
      const date = entry.date;
      let startTime;
      
      if (entry.startTime) {
        startTime = moment(`${date} ${entry.startTime}`, 'YYYY-MM-DD HH:mm');
      } else if (entry.time && entry.time.includes(' - ')) {
        const timeStr = entry.time.split(' - ')[0].trim();
        startTime = timeStr.match(/^\d{1,2}:\d{2}$/) 
          ? moment(`${date} ${timeStr}`, 'YYYY-MM-DD HH:mm')
          : moment(`${date} ${timeStr}`, 'YYYY-MM-DD h:mm A');
      }

      if (startTime && startTime.isValid()) {
        if (!dailyFirstEntries[date] || startTime.isBefore(dailyFirstEntries[date])) {
          dailyFirstEntries[date] = startTime;
        }
      }
    });

    // Check each expected workday up to today
    let current = startDate.clone();
    const today = moment();
    let expectedWorkdays = 0;
    
    while (current.isSameOrBefore(endDate, 'day') && current.isSameOrBefore(today, 'day')) {
      // Only check weekdays
      if (current.day() !== 0 && current.day() !== 6) {
        expectedWorkdays++;
        const dateStr = current.format('YYYY-MM-DD');
        
        if (dailyFirstEntries[dateStr]) {
          // Present - check if on time
          const startTime = dailyFirstEntries[dateStr];
          const startTimeOnly = startTime.clone().set({
            year: standardStartTime.year(),
            month: standardStartTime.month(),
            date: standardStartTime.date()
          });
          
          // Consider on-time if within 15 minutes of standard start time
          if (startTimeOnly.isSameOrBefore(standardStartTime.clone().add(15, 'minutes'))) {
            onTimeCount++;
          }
          // Note: Late attendance and absent days both count as 0 points toward punctuality
        }
      }
      current.add(1, 'day');
    }

    return expectedWorkdays > 0 ? Math.round((onTimeCount / expectedWorkdays) * 100) : 0;
  };

  const calculateProductivityScore = (attendanceData) => {
    if (attendanceData.length === 0) return 0;

    // Group by date and calculate daily hours
    const dailyHours = {};
    attendanceData.forEach(entry => {
      if (!dailyHours[entry.date]) {
        dailyHours[entry.date] = [];
      }
      
      let startTime, endTime;
      if (entry.startTime && entry.endTime) {
        startTime = moment(`${entry.date} ${entry.startTime}`, 'YYYY-MM-DD HH:mm');
        endTime = moment(`${entry.date} ${entry.endTime}`, 'YYYY-MM-DD HH:mm');
      } else if (entry.time && entry.time.includes(' - ')) {
        const [start, end] = entry.time.split(' - ').map(s => s.trim());
        startTime = start.match(/^\d{1,2}:\d{2}$/) 
          ? moment(`${entry.date} ${start}`, 'YYYY-MM-DD HH:mm')
          : moment(`${entry.date} ${start}`, 'YYYY-MM-DD h:mm A');
        endTime = end.match(/^\d{1,2}:\d{2}$/) 
          ? moment(`${entry.date} ${end}`, 'YYYY-MM-DD HH:mm')
          : moment(`${entry.date} ${end}`, 'YYYY-MM-DD h:mm A');
      }

      if (startTime && endTime && startTime.isValid() && endTime.isValid()) {
        if (endTime.isBefore(startTime)) {
          endTime.add(1, 'day');
        }
        const hours = endTime.diff(startTime, 'hours', true);
        if (hours > 0 && hours <= 24) {
          dailyHours[entry.date].push({ start: startTime, end: endTime, hours });
        }
      }
    });

    // Calculate average daily hours and compare to standard (8 hours)
    let totalDays = 0;
    let totalScore = 0;
    const standardHours = 8;

    Object.keys(dailyHours).forEach(date => {
      const dayRanges = dailyHours[date];
      // Merge overlapping ranges
      dayRanges.sort((a, b) => a.start - b.start);
      let mergedHours = 0;
      let lastEnd = null;

      dayRanges.forEach(range => {
        if (!lastEnd || range.start.isAfter(lastEnd)) {
          mergedHours += range.hours;
          lastEnd = range.end;
        } else if (range.end.isAfter(lastEnd)) {
          mergedHours += range.end.diff(lastEnd, 'hours', true);
          lastEnd = range.end;
        }
      });

      // Score based on how close to standard hours (peak score at 8 hours)
      let dayScore;
      if (mergedHours >= standardHours) {
        dayScore = 100;
      } else if (mergedHours >= standardHours * 0.75) { // 6+ hours
        dayScore = 80 + (mergedHours - standardHours * 0.75) / (standardHours * 0.25) * 20;
      } else if (mergedHours >= standardHours * 0.5) { // 4+ hours
        dayScore = 60 + (mergedHours - standardHours * 0.5) / (standardHours * 0.25) * 20;
      } else {
        dayScore = (mergedHours / (standardHours * 0.5)) * 60;
      }

      totalScore += Math.min(100, Math.max(0, dayScore));
      totalDays++;
    });

    return totalDays > 0 ? Math.round(totalScore / totalDays) : 0;
  };

  const generateWeeklyData = (attendanceData, startDate, endDate) => {
    const weeks = [];
    let current = startDate.clone().startOf('week');
    
    while (current.isBefore(endDate)) {
      const weekEnd = current.clone().endOf('week');
      const weekData = attendanceData.filter(entry => {
        const entryDate = moment(entry.date, 'YYYY-MM-DD');
        return entryDate.isBetween(current, weekEnd, null, '[]');
      });

      const weekDays = new Set(weekData.map(entry => entry.date)).size;
      const weekHours = calculateTotalHours(weekData);

      weeks.push({
        week: current.format('MMM D'),
        days: weekDays,
        hours: weekHours,
        performance: weekDays >= 4 ? 'good' : weekDays >= 2 ? 'average' : 'poor'
      });

      current.add(1, 'week');
    }

    return weeks;
  };

  const calculateTotalHours = (attendanceData) => {
    const dailyHours = {};
    
    attendanceData.forEach(entry => {
      if (!dailyHours[entry.date]) {
        dailyHours[entry.date] = [];
      }
      
      let startTime, endTime;
      if (entry.startTime && entry.endTime) {
        startTime = moment(`${entry.date} ${entry.startTime}`, 'YYYY-MM-DD HH:mm');
        endTime = moment(`${entry.date} ${entry.endTime}`, 'YYYY-MM-DD HH:mm');
      } else if (entry.time && entry.time.includes(' - ')) {
        const [start, end] = entry.time.split(' - ').map(s => s.trim());
        startTime = start.match(/^\d{1,2}:\d{2}$/) 
          ? moment(`${entry.date} ${start}`, 'YYYY-MM-DD HH:mm')
          : moment(`${entry.date} ${start}`, 'YYYY-MM-DD h:mm A');
        endTime = end.match(/^\d{1,2}:\d{2}$/) 
          ? moment(`${entry.date} ${end}`, 'YYYY-MM-DD HH:mm')
          : moment(`${entry.date} ${end}`, 'YYYY-MM-DD h:mm A');
      }

      if (startTime && endTime && startTime.isValid() && endTime.isValid()) {
        if (endTime.isBefore(startTime)) {
          endTime.add(1, 'day');
        }
        const hours = endTime.diff(startTime, 'hours', true);
        if (hours > 0 && hours <= 24) {
          dailyHours[entry.date].push({ start: startTime, end: endTime });
        }
      }
    });

    let totalHours = 0;
    Object.keys(dailyHours).forEach(date => {
      const ranges = dailyHours[date];
      ranges.sort((a, b) => a.start - b.start);
      
      let mergedRanges = [];
      ranges.forEach(range => {
        if (mergedRanges.length === 0) {
          mergedRanges.push(range);
        } else {
          const last = mergedRanges[mergedRanges.length - 1];
          if (range.start.isSameOrBefore(last.end)) {
            if (range.end.isAfter(last.end)) {
              last.end = range.end;
            }
          } else {
            mergedRanges.push(range);
          }
        }
      });

      mergedRanges.forEach(range => {
        totalHours += range.end.diff(range.start, 'hours', true);
      });
    });

    return parseFloat(totalHours.toFixed(1));
  };

  const calculateAchievements = (attendanceData, scores) => {
    const achievements = [];

    if (scores.consistencyScore >= 95) {
      achievements.push({
        title: 'Perfect Attendance',
        description: 'Maintained 95%+ attendance rate',
        icon: '🏆',
        color: '#ffd700'
      });
    } else if (scores.consistencyScore >= 85) {
      achievements.push({
        title: 'Consistent Performer',
        description: 'Great attendance consistency',
        icon: '⭐',
        color: '#4caf50'
      });
    }

    if (scores.punctualityScore >= 90) {
      achievements.push({
        title: 'Early Bird',
        description: 'Excellent punctuality record',
        icon: '⏰',
        color: '#2196f3'
      });
    }

    if (scores.productivityScore >= 90) {
      achievements.push({
        title: 'High Performer',
        description: 'Outstanding productivity levels',
        icon: '🚀',
        color: '#ff5722'
      });
    }

    if (scores.overallScore >= 95) {
      achievements.push({
        title: 'Star Employee',
        description: 'Exceptional overall performance',
        icon: '🌟',
        color: '#9c27b0'
      });
    }

    return achievements;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#4caf50';
    if (score >= 75) return '#ff9800';
    if (score >= 60) return '#ffc107';
    return '#f44336';
  };

  const getPerformanceLevel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Good';
    if (score >= 60) return 'Average';
    return 'Needs Improvement';
  };

  useEffect(() => {
    setMetrics(calculateMetrics());
  }, [attendance, timeRange]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Performance Metrics
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Track your attendance patterns and productivity insights
          </Typography>
        </Box>
        
        <ButtonGroup variant="outlined" size="small">
          <Button 
            onClick={() => setTimeRange('week')}
            variant={timeRange === 'week' ? 'contained' : 'outlined'}
          >
            Week
          </Button>
          <Button 
            onClick={() => setTimeRange('month')}
            variant={timeRange === 'month' ? 'contained' : 'outlined'}
          >
            Month
          </Button>
          <Button 
            onClick={() => setTimeRange('quarter')}
            variant={timeRange === 'quarter' ? 'contained' : 'outlined'}
          >
            Quarter
          </Button>
        </ButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {/* Overall Score */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            height: '100%'
          }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                width: 60, 
                height: 60, 
                mx: 'auto', 
                mb: 2 
              }}>
                <StarRate sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                {metrics.overallScore}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                Overall Score
              </Typography>
              <Chip 
                label={getPerformanceLevel(metrics.overallScore)}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Score Breakdown */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Performance Breakdown
            </Typography>
            
            <Grid container spacing={3}>
              {[
                { 
                  label: 'Consistency', 
                  score: metrics.consistencyScore, 
                  icon: <CheckCircle />,
                  description: 'Regular attendance pattern'
                },
                { 
                  label: 'Punctuality', 
                  score: metrics.punctualityScore, 
                  icon: <Schedule />,
                  description: 'On-time arrival rate'
                },
                { 
                  label: 'Productivity', 
                  score: metrics.productivityScore, 
                  icon: <TrendingUp />,
                  description: 'Work hours efficiency'
                }
              ].map((metric, index) => (
                <Grid item xs={12} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ color: getScoreColor(metric.score), mr: 1 }}>
                      {metric.icon}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, flex: 1 }}>
                      {metric.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: getScoreColor(metric.score) }}>
                      {metric.score}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={metric.score} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: '#f1f5f9',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getScoreColor(metric.score),
                        borderRadius: 4
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                    {metric.description}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Weekly Performance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Weekly Performance
            </Typography>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Week</TableCell>
                    <TableCell align="center">Days</TableCell>
                    <TableCell align="center">Hours</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics.weeklyData.map((week, index) => (
                    <TableRow key={index}>
                      <TableCell>{week.week}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={week.days}
                          size="small"
                          icon={<CalendarToday sx={{ fontSize: 16 }} />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${week.hours}h`}
                          size="small"
                          icon={<AccessTime sx={{ fontSize: 16 }} />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={week.performance}
                          size="small"
                          color={
                            week.performance === 'good' ? 'success' : 
                            week.performance === 'average' ? 'warning' : 'error'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Achievements */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Achievements
            </Typography>
            
            {metrics.achievements.length > 0 ? (
              <Grid container spacing={2}>
                {metrics.achievements.map((achievement, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 2, 
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0'
                    }}>
                      <Typography sx={{ fontSize: 24, mr: 2 }}>
                        {achievement.icon}
                      </Typography>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {achievement.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {achievement.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: '#64748b', mb: 2 }}>
                  No achievements yet
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Keep improving your attendance to unlock achievements!
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PerformanceMetrics;